import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom, forkJoin } from 'rxjs';
import { DeviceSettingsChoices, DeviceSettingsReport, DeviceSettingsReportProfile } from '../_interface/deviceSettings';
import { WebsocketResult, WSService } from '../_services/ws.service';
import { SignageService } from '../_services/signage.service';

/**
 * Šta aplikacija daje agentu. Vrednosti i pravila ostaju u settings servisu aplikacije — agent ih samo
 * čita i prosleđuje izmene, pa model `podrazumevano → profil → lokalne izmene` ostaje netaknut.
 */
export interface DeviceSettingsAdapter {
  /** Tip aplikacije ("kd", ...) — isti kodovi kao `DeviceProfile.target`. */
  target: string;
  appVersion: string;
  /** Aplikacija radi u native shell-u (štampa postoji samo tamo). */
  nativeApp: boolean;
  /** Efektivna podešavanja: podrazumevano → profil → lokalne izmene. */
  effective(): { [key: string]: any };
  /** Lokalne izmene jače od profila (null bez profila). */
  overrides(): { [key: string]: any } | null;
  /** Primenjeni profil (null = bez profila, lokalna podešavanja). */
  profile(): DeviceSettingsReportProfile | null;
  /** Primeni kao da su vrednosti unete na uređaju (lokalna izmena jača od profila). */
  apply(values: { [key: string]: any }): void;
  /** Obriši lokalne izmene — podešavanja postaju tačno profil. */
  clearOverrides(): void;
  /**
   * Promeni profil uređaja, kao izbor na ekranu podešavanja (null = bez profila) — opciono, aplikacija
   * bez profila (DS) ga nema. Agent ga čeka pre primene vrednosti.
   */
  selectProfile?(profileID: number | null): Promise<void>;
  /**
   * Izbori koje zna samo uređaj (npr. signage grupe i rasporedi lokala) — opciono. Agent ih čeka
   * najviše `CHOICES_TIMEOUT_MS`, pa izveštaj ide i bez njih.
   */
  choices?(): Promise<DeviceSettingsChoices | null>;
}

/** Rok za izbore — mora da ostane ispod serverskog čekanja na izveštaj (~8 s). */
const CHOICES_TIMEOUT_MS = 3000;

/**
 * Signage grupe i rasporedi lokala kao izbori za admin (izbor „Sadržaj" na CD-u i DS-u) — sirovi
 * spiskovi, natpise gradi admin. Bez lokala nema izbora.
 */
export function loadSignageChoices(signage: SignageService, companyID: number | null | undefined): Promise<DeviceSettingsChoices | null> {
  if (!companyID) {
    return Promise.resolve(null);
  }
  return firstValueFrom(forkJoin({ groups: signage.getGroups(companyID), schedules: signage.getSchedules(companyID) }))
    .then(r => ({
      signageGroups: r.groups.map(g => ({ id: g.groupID, name: g.name })),
      signageSchedules: r.schedules.map(s => ({ id: s.scheduleID, name: s.name })),
    }));
}

/**
 * Kanal podešavanja ka admin portalu (api/deviceSettings) — radi samo dok je uređaj na vezi.
 *
 * - WS `settings.get {target}` → izveštaj `settings.report` sa `requestID`-em zahteva.
 * - WS `settings.set {target, values, resetOverrides, setProfile, profileID}` → (promeni profil) →
 *   (obriši lokalne izmene) → primeni vrednosti kroz aplikaciju, tačno kao da su unete na uređaju →
 *   izveštaj sa `requestID`-em zahteva.
 *
 * Okvir sa tuđim `target`-om se ignoriše: server šalje svim soketima uređaja, a na istom uređaju može
 * biti otvorena i druga aplikacija. Stari `settings_get` (`device/RequestSettings`) je drugi tip okvira
 * i agent ga ne sluša.
 *
 * Aplikacija registruje adapter jednom, pri startu (AppComponent).
 */
@Injectable({ providedIn: 'root' })
export class DeviceSettingsAgentService {
  private adapter?: DeviceSettingsAdapter;

  constructor(private ws: WSService, private logger: NGXLogger) { }

  register(adapter: DeviceSettingsAdapter) {
    if (this.adapter) {
      return;
    }
    this.adapter = adapter;

    this.ws.observe('settings.get').subscribe({
      next: msg => {
        if (msg?.data?.target === adapter.target) {
          this.sendReport(msg);
        }
      }
    });

    this.ws.observe('settings.set').subscribe({
      next: msg => {
        if (msg?.data?.target === adapter.target) {
          this.applyFromPortal(msg);
        }
      }
    });
  }

  private async applyFromPortal(msg: WebsocketResult) {
    const adapter = this.adapter!;
    const data = msg.data || {};
    const values = data.values && typeof data.values === 'object' && !Array.isArray(data.values) ? data.values : {};
    try {
      // Profil prvo: vrednosti iz istog zahteva (npr. ID ekrana) se zatim čuvaju kao lokalna izmena
      // preko NOVOG profila. Bez promene profila nema čekanja — ostatak ide sinhrono.
      if (data.setProfile && adapter.selectProfile) {
        await adapter.selectProfile(data.profileID ?? null);
      }
      if (data.resetOverrides) {
        adapter.clearOverrides();
      }
      if (Object.keys(values).length) {
        adapter.apply(values);
      }
      this.logger.info('Settings changed from portal', {
        keys: Object.keys(values),
        resetOverrides: !!data.resetOverrides,
        profileID: data.setProfile ? (data.profileID ?? null) : undefined,
      });
    } catch (err) {
      this.logger.error('Settings from portal not applied', err);
    } finally {
      // Izveštaj ide i kad primena pukne — admin vidi stvarno stanje uređaja umesto da čeka do isteka.
      this.sendReport(msg);
    }
  }

  private sendReport(msg: WebsocketResult) {
    const adapter = this.adapter;
    if (!adapter || !msg?.requestID) {
      return;
    }
    // Stanje se uzima odmah (posle primene), izbori mogu da stignu kasnije.
    const report: DeviceSettingsReport = {
      target: adapter.target,
      appVersion: adapter.appVersion,
      nativeApp: adapter.nativeApp,
      effective: adapter.effective(),
      overrides: adapter.overrides(),
      profile: adapter.profile(),
      requestID: msg.requestID,
    };
    if (!adapter.choices) {
      this.ws.send({ type: 'settings.report', data: report });
      return;
    }
    this.loadChoices(adapter).then(choices => {
      report.choices = choices;
      this.ws.send({ type: 'settings.report', data: report });
    });
  }

  /** Izbori sa tvrdim rokom — spor ili pao izvor ne sme da zadrži izveštaj; tada idu kao null. */
  private loadChoices(adapter: DeviceSettingsAdapter): Promise<DeviceSettingsChoices | null> {
    const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), CHOICES_TIMEOUT_MS));
    return Promise.race([adapter.choices!().catch(() => null), timeout]);
  }
}
